import { fetchApi } from './api';
import type {
    PRFilterOptions,
    PROverviewData,
    PRVendorRow,
    PRProductRow,
    PRDetailRow,
    PaginationInfo,
} from '../types/purchaseReporting';

export const purchaseReportingApi = {
    async getFilters(): Promise<PRFilterOptions> {
        return fetchApi('/purchase-reporting/filters');
    },

    async getOverview(params: {
        company?: string;
        year: number;
        maxMonth: number;
        dateFrom: string;
        dateTo: string;
        businessUnit?: string;
        vendorID?: string;
        locationID?: string;
    }): Promise<PROverviewData> {
        const sp = new URLSearchParams();
        sp.set('year', String(params.year));
        sp.set('maxMonth', String(params.maxMonth));
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        if (params.company) sp.set('company', params.company);
        if (params.businessUnit) sp.set('businessUnit', params.businessUnit);
        if (params.vendorID) sp.set('vendorID', params.vendorID);
        if (params.locationID) sp.set('locationID', params.locationID);
        return fetchApi(`/purchase-reporting/overview?${sp}`);
    },

    async getByVendor(params: {
        dateFrom: string;
        dateTo: string;
        company?: string;
        businessUnit?: string;
        locationID?: string;
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
    }): Promise<{ items: PRVendorRow[]; pagination: PaginationInfo }> {
        const sp = new URLSearchParams();
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        if (params.company) sp.set('company', params.company);
        if (params.businessUnit) sp.set('businessUnit', params.businessUnit);
        if (params.locationID) sp.set('locationID', params.locationID);
        if (params.sortBy) sp.set('sortBy', params.sortBy);
        if (params.sortOrder) sp.set('sortOrder', params.sortOrder);
        if (params.page) sp.set('page', String(params.page));
        if (params.limit) sp.set('limit', String(params.limit));
        const res = await fetch(`/api/v1/purchase-reporting/by-vendor?${sp}`);
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to fetch vendor data');
        return { items: body.data, pagination: body.pagination };
    },

    async getByProduct(params: {
        dateFrom: string;
        dateTo: string;
        company?: string;
        businessUnit?: string;
        vendorID?: string;
        locationID?: string;
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
    }): Promise<{ items: PRProductRow[]; pagination: PaginationInfo }> {
        const sp = new URLSearchParams();
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        if (params.company) sp.set('company', params.company);
        if (params.businessUnit) sp.set('businessUnit', params.businessUnit);
        if (params.vendorID) sp.set('vendorID', params.vendorID);
        if (params.locationID) sp.set('locationID', params.locationID);
        if (params.sortBy) sp.set('sortBy', params.sortBy);
        if (params.sortOrder) sp.set('sortOrder', params.sortOrder);
        if (params.page) sp.set('page', String(params.page));
        if (params.limit) sp.set('limit', String(params.limit));
        const res = await fetch(`/api/v1/purchase-reporting/by-product?${sp}`);
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to fetch product data');
        return { items: body.data, pagination: body.pagination };
    },

    async getDetail(params: {
        dateFrom: string;
        dateTo: string;
        company?: string;
        businessUnit?: string;
        vendorID?: string;
        locationID?: string;
        search?: string;
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
    }): Promise<{ items: PRDetailRow[]; pagination: PaginationInfo }> {
        const sp = new URLSearchParams();
        sp.set('dateFrom', params.dateFrom);
        sp.set('dateTo', params.dateTo);
        if (params.company) sp.set('company', params.company);
        if (params.businessUnit) sp.set('businessUnit', params.businessUnit);
        if (params.vendorID) sp.set('vendorID', params.vendorID);
        if (params.locationID) sp.set('locationID', params.locationID);
        if (params.search) sp.set('search', params.search);
        if (params.sortBy) sp.set('sortBy', params.sortBy);
        if (params.sortOrder) sp.set('sortOrder', params.sortOrder);
        if (params.page) sp.set('page', String(params.page));
        if (params.limit) sp.set('limit', String(params.limit));
        const res = await fetch(`/api/v1/purchase-reporting/detail?${sp}`);
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error?.message || 'Failed to fetch detail data');
        return { items: body.data, pagination: body.pagination };
    },
};
